import classNames from 'classnames';
import {
  forwardRef,
  useId,
  type ComponentProps,
  type ForwardedRef,
} from 'react';
import {
  useFormContext,
  type FieldError,
  type RegisterOptions,
} from 'react-hook-form';

import { useForkRef } from '@/shared/hooks';
import { cn } from '@/shared/utils/cn.utils';

type Props = ComponentProps<'select'> & {
  label?: string;
  registerOptions?: RegisterOptions;
};

export const Select = forwardRef(
  (
    { className, label, name, registerOptions, ...rest }: Props,
    ref: ForwardedRef<HTMLSelectElement>,
  ) => {
    if (!name) {
      throw new Error('TextField must have a name');
    }

    const id = useId();
    const errorMessageId = useId();
    const { formState, register } = useFormContext();
    const errorMessage = formState.errors[name] as FieldError | undefined;
    const { ref: registeredRef, ...inputPropsFromContext } = register(
      name,
      registerOptions,
    );

    const selectRef = useForkRef(registeredRef, ref);

    return (
      <div className={cn('flex w-full flex-col', className)}>
        {label && (
          <label htmlFor={id} className="text-sm font-normal text-heading">
            {label}
          </label>
        )}
        <select
          className={classNames(
            'h-9 w-full rounded-md border border-subtle bg-white pl-3 pr-8 text-sm text-ink shadow-sm focus:border-accent focus:outline-none disabled:bg-gray-100 disabled:text-gray-500 disabled:opacity-100',
            { 'mt-2': label },
          )}
          ref={selectRef}
          aria-invalid={!!errorMessage}
          id={id}
          aria-describedby={errorMessage ? errorMessageId : undefined}
          {...inputPropsFromContext}
          {...rest}
        />
        {errorMessage && (
          <div
            id={errorMessageId}
            className="w-full bg-red-50 px-3 py-1 text-sm text-red-500"
          >
            {errorMessage.message}
          </div>
        )}
      </div>
    );
  },
);

Select.displayName = 'Select';
